import { type SelectProps } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useListInsurersQuery } from '../../api/queries/listInsurers';
import { type InsurerFilteringRule } from '../../api/types';
import { useCompany } from '../contexts/CompanyContextManager';
import SelectField, { type SelectFieldProps } from './SelectField';

type InsurerSelectSharedProps = {
    skipCondition?: boolean;
    addNoneOption?: boolean;
};

export type InsurerSelectFieldProps = Omit<SelectFieldProps, 'children' | 'options'> &
    Pick<InsurerFilteringRule, 'companyId' | 'moduleId'> &
    InsurerSelectSharedProps;

export type UseInsurerSelectFieldOptionsArgs = InsurerSelectSharedProps & {
    filter: Pick<InsurerFilteringRule, 'companyId' | 'moduleId'>;
};

export const useInsurerSelectFieldOptions = ({
    filter,
    skipCondition,
    addNoneOption,
}: UseInsurerSelectFieldOptionsArgs) => {
    const { t } = useTranslation('moduleDetails');
    const company = useCompany(true);

    const { data, loading } = useListInsurersQuery({
        fetchPolicy: 'cache-and-network',
        variables: { filter: { ...filter, companyId: filter?.companyId || company?.id } },
        skip: skipCondition,
    });

    const { insurers, insurerOptions } = useMemo(() => {
        const results = data?.list?.items || [];

        const options = results.map(result => ({ value: result.id, label: result.displayName }));

        return {
            insurers: results,
            insurerOptions: options,
        };
    }, [data]);

    const options = useMemo(
        () =>
            [
                addNoneOption && { value: null, label: t('moduleDetails:selectFields.options.none') },
                ...insurerOptions,
            ].filter(Boolean),
        [addNoneOption, insurerOptions, t]
    );

    return { insurers, options, loading };
};

const filterOption: SelectProps['filterOption'] = (inputValue, option) =>
    option.label.toString().toLowerCase().includes(inputValue.toLowerCase());

const InsurerSelectField = ({
    companyId,
    moduleId,
    addNoneOption,
    skipCondition,
    ...props
}: InsurerSelectFieldProps) => {
    const { insurers, options, loading } = useInsurerSelectFieldOptions({
        filter: { companyId, moduleId },
        skipCondition,
        addNoneOption,
    });

    return (
        <SelectField
            {...props}
            filterOption={filterOption}
            loading={!insurers && loading}
            options={options}
            showSearch
        />
    );
};

export default InsurerSelectField;
